import { call, select } from 'redux-saga/effects';
import {
  signalingContentLibraryFileMoved,
  signalingContentLibraryFolderMoved,
} from 'features/content-library/actions';
import { selectFolderByLibraryType } from 'features/content-library/contentLibrarySlice';
import { ContentLibraryFolder } from 'features/content-library/types';
import { notification } from 'features/notifications/toast/notification';
import { UserId } from 'features/users/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import i18n from 'i18n';

type ContentLibraryItemMovedAction =
  | ReturnType<typeof signalingContentLibraryFileMoved>
  | ReturnType<typeof signalingContentLibraryFolderMoved>;

export function* onContentLibraryItemMovedSaga(action: ContentLibraryItemMovedAction) {
  const { parentId, personal, initiator } = action.payload;

  const localUserId: UserId = yield select(selectLocalUserId);

  // The broadcast reaches every user holding manage-content, so only the mover gets confirmed.
  // The server omits initiator for moves no participant made, which stay deliberately silent.
  if (initiator?.id !== localUserId) {
    return;
  }

  let destination: string;

  if (parentId) {
    const folder: ContentLibraryFolder | undefined = yield select(
      selectFolderByLibraryType,
      parentId,
      personal ? 'personal' : 'room'
    );

    // A client that never loaded the destination folder cannot name it, so it stays quiet.
    if (!folder?.name) {
      return;
    }

    destination = folder.name;
  } else {
    // The root folder's name is the literal '/', so the library's tab label stands in for it.
    destination = i18n.t(
      personal
        ? 'content-library:tabs.personal_library.tab_label'
        : 'content-library:tabs.room_library.tab_label'
    );
  }

  // Only the file copy calls the destination a folder, so the root needs a variant without it.
  const fileCopy = parentId
    ? 'notifications:content_library.file_moved'
    : 'notifications:content_library.file_moved_to_root';

  const copy = signalingContentLibraryFolderMoved.match(action)
    ? 'notifications:content_library.folder_moved'
    : fileCopy;

  yield call(notification, i18n.t(copy, { destination }));
}
